import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, Target, Clock, HelpCircle, Info } from "lucide-react";
import { toast } from "sonner";

interface OCDToolsProps {
  user: any;
}

export const OCDTools = ({ user }: OCDToolsProps) => {
  const [activeTab, setActiveTab] = useState('erp');

  const tools = [
    {
      id: 'erp',
      name: 'ERP Tracker',
      icon: Target,
      description: 'Exposure and Response Prevention - the gold standard treatment for OCD',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'OCD traps you in a cycle: obsession (intrusive thought) → anxiety → compulsion (ritual) → temporary relief → stronger obsession. Compulsions feed OCD by teaching your brain that the obsession was a real threat. ERP breaks this cycle by preventing the compulsion, which teaches your brain the obsession isn\'t dangerous.',
      how: 'Create an exposure hierarchy (list of feared situations from easiest to hardest). Start with moderate difficulty. Expose yourself to the trigger WITHOUT doing the compulsion. Stay with the anxiety-it will peak and then decrease naturally. Track your anxiety ratings every 5 minutes to see it drop. Repeat daily.',
      science: 'ERP works through "habituation"-your anxiety naturally decreases when you stay with it long enough without performing compulsions. It also works through "inhibitory learning"-teaching your brain that the feared outcome doesn\'t happen. 60-70% of people who complete ERP experience significant reduction in symptoms.'
    },
    {
      id: 'hierarchy',
      name: 'Hierarchy Builder',
      icon: Target,
      description: 'Create your personalised exposure hierarchy for ERP work',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'Trying to face your worst fear first often leads to feeling overwhelmed and giving up. A hierarchy lets you build confidence gradually by starting with manageable challenges and working your way up. Success breeds success.',
      how: 'List 10-15 situations that trigger your OCD, from mild to severe. Rate each 0-100 for anxiety. Examples: "Touch doorknob without washing (30)", "Leave house without checking stove (50)", "Don\'t reread email 5 times (70)". Start with items rated 40-60. Master one level before moving up.',
      science: 'Gradual exposure builds self-efficacy and allows your brain to process the inhibitory learning more effectively. Starting too high activates your nervous system beyond your window of tolerance, making learning harder. The hierarchy should feel challenging but not overwhelming.'
    },
    {
      id: 'ritual-delays',
      name: 'Ritual Delays',
      icon: Clock,
      description: 'Gradually lengthen the time between obsession and compulsion',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      why: 'If stopping a compulsion completely feels impossible, delaying it is a powerful first step. Delays show you that anxiety peaks and drops naturally, even without immediate rituals. Each successful delay weakens OCD\'s grip.',
      how: 'When the urge to do a compulsion arises, delay it by 5 minutes first. Use this time to notice anxiety rise and fall. Track how you feel. Gradually increase delays: 5 min → 10 min → 20 min → 1 hour. Eventually, you may find you don\'t need the ritual at all.',
      science: 'Delaying compulsions demonstrates that anxiety has a natural peak and decline (typically 20-30 minutes). This breaks the learned association between compulsion and relief. It\'s often easier psychologically than complete response prevention and serves as a stepping stone.'
    },
    {
      id: 'uncertainty',
      name: 'Uncertainty Tolerance',
      icon: HelpCircle,
      description: 'Build capacity to sit with "not knowing for sure"',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      why: 'OCD seeks 100% certainty, which is impossible. Compulsions temporarily reduce doubt ("Did I lock the door? Let me check 10 times to be sure"). But this makes you less able to tolerate uncertainty. Recovery means learning to live with "I\'m 95% sure I locked it" and moving on.',
      how: 'Practice sitting with uncertainty in low-stakes situations first: "Maybe I spelled that wrong in my text-oh well," "I might have left a light on-that\'s okay." Use the phrase "Maybe, maybe not" instead of seeking reassurance. Notice that uncertainty is uncomfortable but not dangerous.',
      science: 'Intolerance of uncertainty is a core maintaining factor in OCD. By repeatedly sitting with doubt without seeking resolution, you build distress tolerance and reduce the need for certainty-seeking behaviours. This generalizes to other areas of life affected by OCD.'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-br from-background to-primary/5 border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-lg bg-primary/10">
              <RefreshCw className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl">OCD Management Tools</CardTitle>
              <CardDescription>Evidence-based ERP and compulsion management</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-background/50 backdrop-blur-sm rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              Understanding OCD
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              OCD is not about being tidy or perfectionistic-it\'s about intrusive, unwanted thoughts (obsessions) 
              and the rituals used to reduce anxiety (compulsions). These tools focus on Exposure and Response 
              Prevention (ERP), the most effective treatment for OCD. ERP should ideally be done with a trained 
              therapist, but these tools can support your work.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tools Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 bg-gradient-calm border border-border/50">
          {tools.map((tool) => {
            const Icon = tool.icon;
            return (
              <TabsTrigger 
                key={tool.id} 
                value={tool.id}
                className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tool.name.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {tools.map((tool) => {
          const Icon = tool.icon;
          return (
            <TabsContent key={tool.id} value={tool.id} className="mt-6 space-y-6">
              <Card className="border-border/50">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${tool.bgColor}`}>
                      <Icon className={`h-5 w-5 ${tool.color}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{tool.name}</CardTitle>
                      <CardDescription>{tool.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Why This Matters */}
                  <div className="bg-gradient-to-br from-primary/20 to-secondary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">💡 Why This Matters</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.why}
                    </p>
                  </div>

                  {/* How to Use It */}
                  <div className="bg-gradient-to-br from-secondary/20 to-primary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🔧 How to Use This Tool</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.how}
                    </p>
                  </div>

                  {/* The Science */}
                  <div className="bg-gradient-to-br from-primary/20 to-secondary/15 rounded-lg p-4 space-y-2">
                    <p className="text-base font-medium text-foreground font-aileron">🧠 The Science</p>
                    <p className="text-sm text-foreground leading-relaxed">
                      {tool.science}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => toast.info(`${tool.name} interactive exercise is being developed. Check back soon!`)}
                    >
                      Start Exercise
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 text-base font-aileron" 
                      onClick={() => toast.info(`${tool.name} examples coming soon!`)}
                    >
                      View Examples
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Important Notes */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="pt-6">
          <div className="space-y-3">
            <p className="text-sm font-medium">Essential ERP Principles:</p>
            <div className="space-y-2 text-xs text-foreground">
              <p><strong>1. Expect discomfort:</strong> ERP should make you moderately anxious. If it doesn\'t, you\'re not targeting OCD effectively.</p>
              <p><strong>2. No reassurance:</strong> Seeking reassurance ("Did I do this right?" "Am I sure it\'s safe?") is a subtle compulsion. Resist it.</p>
              <p><strong>3. Daily practice:</strong> ERP works best with consistent, repeated exposures. Once isn\'t enough to retrain your brain.</p>
              <p><strong>4. Be patient:</strong> Anxiety spikes initially (first 2-3 weeks) before improving. This is normal and means it\'s working.</p>
              <p><strong>5. Get professional help:</strong> ERP is most effective with a therapist trained in OCD treatment. These tools complement, not replace, therapy.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};